import { useLayoutEffect, useRef, useState } from "react";
import "./ReadMoreText.css";

const ReadMoreText = ({ text = "" }) => {
  const containerRef = useRef(null);
  const clampRef = useRef(null);
  const fullRef = useRef(null);
  const contentRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [measureWidth, setMeasureWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      const rect = containerRef.current.getBoundingClientRect();
      setMeasureWidth(Math.floor(rect.width || 0));
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useLayoutEffect(() => {
    if (!clampRef.current || !fullRef.current) return;

    const clampedHeight = clampRef.current.getBoundingClientRect().height;
    const fullHeight = fullRef.current.getBoundingClientRect().height;
    const hasOverflow = fullHeight - clampedHeight > 1;

    setIsOverflowing(hasOverflow);
    if (!hasOverflow && isExpanded) {
      setIsExpanded(false);
    }
  }, [text, measureWidth, isExpanded]);

  const contentClassName = isExpanded
    ? "read-more-text__content read-more-text__content--expanded"
    : "read-more-text__content read-more-text__content--clamped";

  const handleToggle = () => {
    setIsExpanded((prev) => {
      const next = !prev;
      if (!next && contentRef.current) {
        requestAnimationFrame(() => {
          if (contentRef.current) {
            contentRef.current.scrollTop = 0;
          }
        });
      }
      return next;
    });
  };

  return (
    <div className="read-more-text" ref={containerRef}>
      <div className={contentClassName} ref={contentRef}>
        {text}
      </div>
      {isOverflowing && (
        <button
          type="button"
          className="read-more-text__toggle"
          onClick={handleToggle}
          aria-expanded={isExpanded}
        >
          {isExpanded ? "...show less" : "...show more"}
        </button>
      )}
      <div
        className="read-more-text__measure"
        style={{ width: measureWidth || "auto" }}
        aria-hidden="true"
      >
        <div
          ref={clampRef}
          className="read-more-text__content read-more-text__content--clamped"
        >
          {text}
        </div>
        <div ref={fullRef} className="read-more-text__content">
          {text}
        </div>
      </div>
    </div>
  );
};

export default ReadMoreText;
